import type { GpsCoordinates } from "../gpsService";
import { parseActivityTriggerFeatures } from "./parser";
import type {
	ActivityTriggerAction,
	ActivityTriggerCapture,
	ActivityTriggerControllerCandidate,
	ActivityTriggerFeature,
	ActivityTriggerField,
	ActivityTriggerFieldId,
	ActivityTriggerOverlap,
	ActivityTriggerRoleCode,
	ActivityTriggerValueCapability,
	ParsedActivityTriggerFeature
} from "./types";

interface ActivityTriggerValueController extends ActivityTriggerControllerCandidate {
	field: ActivityTriggerField;
	readonly capabilities: {
		readonly activityTrigger: ActivityTriggerValueCapability;
	};
}

interface ActivityTriggerLogger {
	warn(message: string, details: { overlaps: ActivityTriggerOverlap[] }): void;
}

export interface ActivityTriggerCoordinatorOptions {
	features?: ActivityTriggerFeature[];
	fields?: ActivityTriggerField[];
	getControllers: () => ActivityTriggerControllerCandidate[];
	getCurrentPosition: () => PromiseLike<GpsCoordinates>;
	isReadonly?: () => boolean;
	logger?: ActivityTriggerLogger;
	now?: () => Date;
}

function isControllerSupported(controller: ActivityTriggerControllerCandidate | undefined): controller is ActivityTriggerValueController {
	return typeof controller?.capabilities?.activityTrigger?.hasValue === "boolean"
		&& typeof controller.capabilities.activityTrigger.captureValue === "function"
		&& typeof controller.capabilities.activityTrigger.applyCapturedValue === "function"
		&& controller.field !== undefined;
}

export class ActivityTriggerCoordinator {
	readonly valid: boolean;
	readonly features: ParsedActivityTriggerFeature[];
	readonly actions: ActivityTriggerAction[];

	private readonly fields: ActivityTriggerField[];
	private readonly getControllers: () => ActivityTriggerControllerCandidate[];
	private readonly getCurrentPosition: () => PromiseLike<GpsCoordinates>;
	private readonly isReadonly?: () => boolean;
	private readonly now: () => Date;
	private readonly actionsByFieldId: Map<ActivityTriggerFieldId, ActivityTriggerAction[]>;
	private readonly actionsBySectionId: Map<ActivityTriggerFieldId, ActivityTriggerAction[]>;
	private readonly pendingActions = new Set<ActivityTriggerAction>();

	constructor({
		features,
		fields,
		getControllers,
		getCurrentPosition,
		isReadonly,
		logger,
		now
	}: ActivityTriggerCoordinatorOptions) {
		const parsed = parseActivityTriggerFeatures(features);
		this.valid = parsed.valid;
		this.features = parsed.features;
		this.fields = fields ?? [];
		this.getControllers = getControllers;
		this.getCurrentPosition = getCurrentPosition;
		this.isReadonly = isReadonly;
		this.now = now ?? (() => new Date());

		if(!parsed.valid) {
			logger?.warn("Overlapping ActivityTrigger field mappings disable all activity triggers", {
				overlaps: parsed.overlaps
			});
		}

		this.actions = this.features.flatMap(feature => feature.groups);
		this.actionsBySectionId = this.buildActionsBySectionId();
		this.actionsByFieldId = this.buildActionsByFieldId();
	}

	private buildActionsByFieldId(): Map<ActivityTriggerFieldId, ActivityTriggerAction[]> {
		return this.actions.reduce((actionsByFieldId, action) => {
			action.fieldIds.forEach(fieldId => {
				const fieldActions = actionsByFieldId.get(fieldId) ?? [];
				fieldActions.push(action);
				actionsByFieldId.set(fieldId, fieldActions);
			});

			return actionsByFieldId;
		}, new Map<ActivityTriggerFieldId, ActivityTriggerAction[]>());
	}

	private buildActionsBySectionId(): Map<ActivityTriggerFieldId, ActivityTriggerAction[]> {
		return this.actions.reduce((actionsBySectionId, action) => {
			const actionFields = action.fieldIds.map(fieldId => this.fields.find(field => field.Id === fieldId));
			if(actionFields.some(field => !field || field.Pivot)) {
				return actionsBySectionId;
			}

			const sectionId = actionFields[0]?.Section?.Id;
			if(sectionId === undefined
				|| actionFields.some(field => field?.Section?.Id !== sectionId)) {
				return actionsBySectionId;
			}

			const sectionActions = actionsBySectionId.get(sectionId) ?? [];
			sectionActions.push(action);
			actionsBySectionId.set(sectionId, sectionActions);
			return actionsBySectionId;
		}, new Map<ActivityTriggerFieldId, ActivityTriggerAction[]>());
	}

	getFieldActions(fieldId: ActivityTriggerFieldId): ActivityTriggerAction[] {
		return this.actionsByFieldId.get(fieldId) ?? [];
	}

	getSectionActions(sectionId: ActivityTriggerFieldId): ActivityTriggerAction[] {
		return this.actionsBySectionId.get(sectionId) ?? [];
	}

	private getFeatureGroup(action: ActivityTriggerAction, roleCode: ActivityTriggerRoleCode): ActivityTriggerAction | undefined {
		return this.features
			.find(feature => feature.featureId === action.featureId)
			?.groups.find(group => group.code === roleCode);
	}

	private getGroupControllers(action: ActivityTriggerAction): Array<ActivityTriggerValueController | undefined> {
		const registeredControllers = this.getControllers();
		return action.fieldIds.map(fieldId => {
			const matchingControllers = registeredControllers.filter(controller =>
				controller.field?.Id === fieldId);

			const controller = matchingControllers.length === 1 ? matchingControllers[0] : undefined;
			return isControllerSupported(controller) ? controller : undefined;
		});
	}

	private areControllersAvailable(controllers: Array<ActivityTriggerValueController | undefined>): controllers is ActivityTriggerValueController[] {
		return controllers.every(controller => controller
			&& !controller.field.isReadonly
			&& !controller.field.isDisabled);
	}

	private isGroupEmpty(action: ActivityTriggerAction): boolean {
		const controllers = this.getGroupControllers(action);
		return this.areControllersAvailable(controllers)
			&& controllers.every(controller => !controller.capabilities.activityTrigger.hasValue);
	}

	private isGroupPopulated(action: ActivityTriggerAction): boolean {
		const controllers = this.getGroupControllers(action);
		return this.areControllersAvailable(controllers)
			&& controllers.every(controller => controller.capabilities.activityTrigger.hasValue);
	}

	private isStateAvailable(action: ActivityTriggerAction): boolean {
		if(!this.valid || this.isReadonly?.() === true || !this.isGroupEmpty(action)) {
			return false;
		}

		const startAction = this.getFeatureGroup(action, "start");
		const endAction = this.getFeatureGroup(action, "end");

		if(action.code === "start") {
			return !endAction || this.isGroupEmpty(endAction);
		}

		if(action.code === "end") {
			return !startAction || this.isGroupPopulated(startAction);
		}

		return true;
	}

	private isPending(action: ActivityTriggerAction): boolean {
		return this.pendingActions.has(action);
	}

	private setPending(action: ActivityTriggerAction, pending: boolean): void {
		if(pending) {
			this.pendingActions.add(action);
			return;
		}

		this.pendingActions.delete(action);
	}

	isAvailable(action: ActivityTriggerAction): boolean {
		return !this.isPending(action) && this.isStateAvailable(action);
	}

	private createCapture(): ActivityTriggerCapture {
		let currentPosition: Promise<GpsCoordinates> | undefined;
		return {
			now: this.now(),
			getCurrentPosition: () => {
				currentPosition = currentPosition ?? Promise.resolve(this.getCurrentPosition());
				return currentPosition;
			}
		};
	}

	async apply(action: ActivityTriggerAction): Promise<boolean> {
		if(!this.isAvailable(action)) {
			return false;
		}

		const controllers = this.getGroupControllers(action);
		if(!this.areControllersAvailable(controllers)) {
			return false;
		}

		this.setPending(action, true);
		try {
			const capture = this.createCapture();
			const values = await Promise.all(
				controllers.map(controller => controller.capabilities.activityTrigger.captureValue(capture))
			);
			const currentControllers = this.getGroupControllers(action);

			if(!this.areControllersAvailable(currentControllers)
				|| currentControllers.some((controller, index) => controller !== controllers[index])
				|| !this.isStateAvailable(action)) {
				return false;
			}

			controllers.forEach((controller, index) => {
				controller.capabilities.activityTrigger.applyCapturedValue(values[index]);
			});
			return true;
		}
		finally {
			this.setPending(action, false);
		}
	}
}
